import {
  DownloadOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { PlusIcon } from "@radix-ui/react-icons";
import { Button, Form, List, Modal, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { RoundButtonWithIcon } from "../../../components/AntCostomComponents/Buttons";
import {
  ModalTextArea,
  ModalUpload,
} from "../../../components/AntCostomComponents/Inputs";
import {
  createVisit,
  downloadAttachment,
  getRecentVisits,
  getVisit,
} from "../../../utils/api-lists/visit.api";
import { convertUtcIso, formatDate, formatUtcToLocal } from "../../../utils/date.util";

const { Text } = Typography;

const VisitsHistory = ({ visits, onUpdate }) => {
  const { patientId } = useParams();
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visitDetails, setVisitDetails] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { t } = useTranslation("common");

  const [form] = Form.useForm();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();

      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const day = now.getDate();
      const hour = now.getHours();
      const minute = now.getMinutes();
      setSubmitting(true);

      // Format: yyyy-M-d HH:mm
      const date = `${year}-${month}-${day} ${hour}:${minute}`;

      // Convert to UTC ISO with +00:00 instead of Z
      const isoDate = convertUtcIso(date);

      // Extract file objects from fileList
      const attachments = fileList.map((file) => file.originFileObj || file);

      await createVisit(patientId, values.note, isoDate, attachments);
      console.log("Visit created successfully");

      const updatedVisits = await getRecentVisits(patientId);
      onUpdate(updatedVisits);
      setShowPopup(false);
      setFileList([]);
      form.resetFields();
      
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
    finally {
      setSubmitting(false);
    }
  };

  const iconByExt = (ext) => {
    if (ext === "pdf")
      return <FilePdfOutlined style={{ color: "#ef4444", fontSize: 22 }} />;
    if (ext === "jpeg" || ext === "jpg")
      return <FileImageOutlined style={{ color: "#22c55e", fontSize: 22 }} />;
    return <FileOutlined style={{ color: "#a78bfa" }} />;
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleVisitClick = async (visit) => {
    try {
      setSelectedVisit(visit);
      setShowDetailsModal(true);
      // Fetch detailed visit data including attachments
      const details = await getVisit(patientId, visit.id);
      setVisitDetails(details);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching visit details:", err);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      await downloadAttachment(
        patientId,
        selectedVisit.id,
        attachment.id,
        attachment.filename
      );
    } catch (err) {
      setError(err.message);
      console.error("Error downloading attachment:", err);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedVisit(null);
    setVisitDetails(null);
  };

  return (
    <div className="mt-6 bg-[--accent-primary] rounded-xl shadow-sm p-6 border border-[--accent-secondary]">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Medical History</h2>
        <RoundButtonWithIcon
          text={t("add_visit")}
          icon={PlusIcon}
          onClick={() => setShowPopup(true)}
        />

        {/* Add Visit Modal */}
        <Modal
          title={t("add_visit")}
          open={showPopup}
          className="custom-dark-modal"
          onCancel={() => {
            setShowPopup(false);
            setFileList([]);
            form.resetFields();
          }}
          footer={null}
          width={700}
          disabled={submitting}
        >
          <Form
            layout="vertical"
            form={form}
            initialValues={{
              note: "",
            }}
          >
            <Form.Item
              name="note"
              label={t("note")}
              validateTrigger="onBlur"
              rules={[
                { required: true, message: "Please enter a note" },
                {
                  min: 10,
                  message: t("note_min_length", { minLength: 10 }),
                },
              ]}
            >
              <ModalTextArea />
            </Form.Item>

            <Form.Item name="attachments" label={t("attachments")}>
              <ModalUpload
                uploadHint={t("drag_drop_attachments")}
                supportTypeHint={t("support_type_hint")}
                multiple
                fileList={fileList}
                onChange={handleFileChange}
                accept="image/jpeg,image/jpg,application/pdf"
              />
            </Form.Item>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </Form>

          <div className="mt-4 flex justify-end">
            <Button
              type="primary"
              onClick={handleSubmit}
              style={{ backgroundColor: "#8e44ad", borderColor: "#8e44ad" }}
              loading={submitting}
            >
              {t("add")}
            </Button>
          </div>
        </Modal>

        {/* Visit Details Modal */}
        <Modal
          title={t("visit_details")}
          open={showDetailsModal}
          className="custom-dark-modal"
          onCancel={closeDetailsModal}
          footer={[
            <Button
              key="close"
              type="primary"
              style={{
                backgroundColor: "#8e44ad",
                borderColor: "#8e44ad",
                marginTop: 10,
              }}
              onClick={closeDetailsModal}
            >
              {t("close")}
            </Button>,
          ]}
          width={800}
        >
          {selectedVisit && (
            <div className="space-y-4">
              <div>
                <Text strong className="text-white">
                  {t("date")}:
                </Text>
                <div className="mt-1">
                  {formatUtcToLocal(selectedVisit.date).toLocaleUpperCase()}
                </div>
              </div>

              <div>
                <Text strong style={{ color: "#fff" }}>
                  {t("note")}:
                </Text>

                <div
                  style={{
                    marginTop: 6,
                    padding: 14,
                    background: "#2a2739", 
                    border: "1px solid #3b394a", 
                    borderRadius: 12,
                    color: "#e8e6f2", 
                    whiteSpace: "pre-wrap", 
                    wordBreak: "break-word", 
                  }}
                >
                  {selectedVisit.note || t("no_note")}
                </div>
              </div>

              {visitDetails &&
                visitDetails.attachments &&
                visitDetails.attachments.length > 0 && (
                  <div>
                    <Text strong className="text-white">
                      {t("attachments")}:
                    </Text>

                    <List
                      className="mt-2"
                      size="small"
                      dataSource={visitDetails.attachments.map((a) => {
                        try {
                          return JSON.parse(a);
                        } catch {
                          return a;
                        }
                      })}
                      renderItem={(attachment) => (
                        <List.Item
                          style={{
                            background: "#2a2739",
                            border: "1px solid #3b394a",
                            borderRadius: 12,
                            padding: "12px 10px",
                            marginBottom: 8,
                          }}
                          actions={[
                            <RoundButtonWithIcon
                              text={t("download")}
                              icon={DownloadOutlined}
                              onClick={() =>
                                window.open(attachment.url, "_blank")
                              }
                              textColor="#38BDF8"
                              color="#38BDF8"
                            />,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={iconByExt(attachment.ext)}
                            title={
                              <span className="text-white">
                                {attachment.name}
                              </span>
                            }
                            description={
                              <span className="text-gray-400">
                                {attachment.size &&
                                  `${(attachment.size / 1024).toFixed(1)} KB`}
                                {attachment.type && ` • ${attachment.type}`}
                              </span>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                )}

              {visitDetails &&
                (!visitDetails.attachments ||
                  visitDetails.attachments.length === 0) && (
                  <div>
                    <Text strong className="text-white">
                      {t("attachments")}:
                    </Text>
                    <div className="mt-2 text-gray-400 text-sm">
                      {t("no_attachments")}
                    </div>
                  </div>
                )}
            </div>
          )}
        </Modal>
      </div>

      <div className="space-y-4">
        <div className="space-y-4">
          {visits.length > 0 ? (
            visits.map((entry, index) => (
              <div
                key={index}
                className="p-4 border border-[--accent-secondary] rounded-[--radius] cursor-pointer hover:bg-[#48474E] transition-colors"
                onClick={() => handleVisitClick(entry)}
              >
                <div className="flex justify-between items-start">
                  <div className="text-sm font-medium">
                    {formatUtcToLocal(entry.date).toLocaleUpperCase()}
                  </div>
                </div>
                <p className="mt-2 text-[--text-secondary]">{entry.note}</p>
                {entry.attachmentCount && entry.attachmentCount > 0 && (
                  <div className="mt-2 flex items-center text-xs text-purple-400">
                    <FileOutlined className="mr-1" />
                    {entry.attachmentCount}{" "}
                    {entry.attachmentCount === 1
                      ? t("attachment")
                      : t("attachments")}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-[--text-secondary] text-center">
              {t("no_medical_history")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitsHistory;
